import fetch from './index'

export type IdpWebKey = 'FG' | 'ST' | 'BF'

export interface IdpOverviewDTO {
  webKey: IdpWebKey
  pv: number
  uv: number
  sessions?: number
  clicks: number
  dau: number
  wau: number
  mau: number
  trend?: Array<{ day: string; pv: number; uv: number; clicks: number }>
  identityDistribution?: Array<{ name: string; value: number }>
  deviceDistribution?: Array<{ name: string; value: number }>
  eventTypeDistribution?: Array<{ name: string; value: number }>
}

export interface IdpRealtimeEventDTO {
  eventId: string
  eventTime: number
  eventType: string
  webKey: IdpWebKey
  deviceId: string
  sessionId: string
  customerId?: string
  deviceType?: string
  pageId?: number
  pageUrl?: string
  elementId?: number
  elementText?: string
  referrer?: string
  scrollDepth?: number
  engagementMs?: number
  props?: Record<string, any>
}

export interface IdpSessionDetailDTO {
  sessionId: string
  deviceId: string
  customerId?: string
  deviceType?: string
  entryUrl?: string
  exitUrl?: string
  startedAt?: number
  endedAt?: number
  durationMs?: number
  eventCount?: number
  events?: IdpRealtimeEventDTO[]
}

export interface IdpSessionSummaryDTO {
  sessionId: string
  deviceId: string
  customerId?: string
  deviceType?: string
  entryUrl?: string
  exitUrl?: string
  startedAt?: number
  endedAt?: number
  durationMs?: number
  eventCount?: number
  pageViewCount?: number
  entryPageId?: number
  exitPageId?: number
  hasAtc?: boolean
  hasPurchase?: boolean
  hasInquiry?: boolean
}

export interface IdpSessionListDTO {
  webKey: IdpWebKey
  cursor?: string
  nextCursor?: string
  pageSize?: number
  totalCount?: number
  sessions?: IdpSessionSummaryDTO[]
  detail?: IdpSessionDetailDTO
}

export interface IdpFunnelDTO {
  webKey: IdpWebKey
  steps?: Array<{
    stepIndex: number
    stepKey: string
    label: string
    users: number
    conversionRate: number
    dropOffRate: number
  }>
}

export interface IdpDataHealthDTO {
  webKey: IdpWebKey
  healthy: boolean
  kpis?: Array<{ name: string; status: string; value: number; target: number }>
  rollupLags?: Array<{ tableName: string; watermarkTime?: number; lagSeconds?: number; status: string }>
  fieldFillRates?: Array<{ fieldName: string; fillRate: number; status: string }>
}

interface IdpBaseParams {
  webKey: IdpWebKey
  from?: string
  to?: string
}

function cleanParams(params: Record<string, any>) {
  return Object.keys(params).reduce((acc, key) => {
    const value = params[key]
    if (value !== undefined && value !== null && value !== '') {
      acc[key] = value
    }
    return acc
  }, {} as Record<string, any>)
}

function serializeRepeatedParams(params: Record<string, any>) {
  const search = new URLSearchParams()
  Object.keys(params || {}).forEach(key => {
    const value = params[key]
    if (Array.isArray(value)) {
      value.forEach(item => search.append(key, String(item)))
      return
    }
    search.append(key, String(value))
  })
  return search.toString()
}

export function getIdpOverview(params: IdpBaseParams): Promise<IdpOverviewDTO> {
  return fetch.get('/idp/analytics/overview', {
    params: cleanParams(params),
    noLoading: true,
  })
}

export function getIdpSessions(params: IdpBaseParams & {
  filters?: Record<string, any>
  cursor?: string
  sessionId?: string
}): Promise<IdpSessionListDTO> {
  return fetch.get('/idp/analytics/sessions', {
    params: cleanParams({
      ...params,
      filters: params.filters ? JSON.stringify(params.filters) : undefined,
    }),
    // axios 默认序列化会把 %5B/%5D 还原成裸 [],gateway Tomcat 拒裸方括号(400 无 CORS 头,
    // 浏览器表现为 NetworkError)——数组型筛选(时长/转化)的 filters JSON 必踩;走全编码序列化
    paramsSerializer: serializeRepeatedParams,
    noLoading: true,
  })
}

export function getIdpFunnel(params: IdpBaseParams & { steps?: string[] }): Promise<IdpFunnelDTO> {
  return fetch.get('/idp/analytics/funnel', {
    params: cleanParams(params),
    paramsSerializer: serializeRepeatedParams,
    noLoading: true,
  })
}

export interface IdpAggregateRow {
  elementId?: number
  selector?: string
  elementText?: string
  semanticRole?: string
  eventType?: string
  pv: number
  uv: number
}

export interface IdpAggregateDTO {
  webKey: IdpWebKey
  rows?: IdpAggregateRow[]
}

export interface IdpPathFlowNode {
  pageId: number
  url?: string
  title?: string
  pv: number
  exits: number
}

export interface IdpSankeyNode {
  id: string
  label: string
  depth: number
  users: number
  pageType?: string
}

export interface IdpPathFlowDTO {
  webKey: IdpWebKey
  nodes?: IdpPathFlowNode[]
  links?: Array<{ sourcePageId: number; targetPageId: number; value: number }>
  sankeyNodes?: IdpSankeyNode[]
  sankeyLinks?: Array<{ source: string; target: string; value: number }>
  sankeyTotalStart?: number
  truncated?: boolean // true=数据量超上界被截断,提示缩小日期范围
}

export function getIdpAggregate(params: IdpBaseParams & {
  deviceBucket?: string
  destFunction?: string
  // 点击所在页面 URL 模糊过滤（服务端 LIKE，行数据无 URL 维度所以前端筛不了）
  urlContains?: string
}): Promise<IdpAggregateDTO> {
  return fetch.get('/idp/analytics/aggregate', {
    params: cleanParams(params),
    noLoading: true,
  })
}

export function getIdpDestFunctions(params: { webKey: IdpWebKey }): Promise<string[]> {
  return fetch.get('/idp/analytics/destfunctions', {
    params: cleanParams(params),
    noLoading: true,
  })
}

export function getIdpPathFlow(params: IdpBaseParams & {
  startPageId?: number
  startPageType?: string
  targetPageId?: number
  maxDepth?: number
  startUrl?: string
  targetUrl?: string
  // 动作目标点/起点:'atc'|'checkout'|'purchase'|'inquiry';targetAction 与 targetPageId/targetUrl 互斥(同传后端报错)
  targetAction?: string
  startAction?: string
  // 只统计路径经过该类型节点的 session:'HOMEPAGE'|'COLLECTION'|'PDP'|'CART'|'CHECKOUT'|'BLOG'|'ACTION'
  containsType?: string
  // 节点粒度:'type'(默认,按页面类型归并成首页/商品页/集合页总览)|'page'(保留具体页,下钻看具体来源,每层 cap top 15)
  nodeGranularity?: string
}): Promise<IdpPathFlowDTO> {
  return fetch.get('/idp/analytics/pathflow', {
    params: cleanParams(params),
    noLoading: true,
  })
}

export interface IdpCohortWeek {
  cohortDate: string
  cohortSize: number
  d0?: number | null
  d1?: number | null
  d3?: number | null
  d7?: number | null
  d14?: number | null
  d30?: number | null
}

export interface IdpCohortDTO {
  webKey: IdpWebKey
  cohorts?: IdpCohortWeek[]
}

export function getIdpCohort(params: { webKey: IdpWebKey; weeks?: number }): Promise<IdpCohortDTO> {
  return fetch.get('/idp/analytics/cohort', {
    params: cleanParams(params),
    noLoading: true,
  })
}

export function getIdpRealtime(params: {
  webKey: IdpWebKey
  limit?: number
}): Promise<IdpRealtimeEventDTO[]> {
  return fetch.get('/idp/analytics/realtime', {
    params: cleanParams(params),
    noLoading: true,
  })
}

export function getIdpHealth(params: { webKey: IdpWebKey }): Promise<IdpDataHealthDTO> {
  return fetch.get('/idp/health/snapshot', {
    params,
    noLoading: true,
  })
}
